import {
  MessageDirection,
  MessagesService,
  MessageType,
  OrgMessageRecipient,
  PaymentsService,
} from "client/openapi";
import type { PersonRow } from "client/openapi/models/PersonRow";
import { Button, ButtonColor, ButtonFill } from "components/Button";
import { Dialog, DialogContent } from "components/Dialog";
import { useContext, useEffect, useState } from "react";
import Notifications from "util/notifications";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { personName } from "./person";

/**
 * A tutor's Payroll step. Done: their payroll bank is set up. Not yet: their
 * bank setup link (the Stripe link behind "Get Verification Link"), to copy
 * or to send them by text and email. The link needs the tutor's Tadpole
 * account, so without one both wait, saying why.
 */
export default function BankLinkDialog({
  tutor,
  hasAccount,
  done = false,
  orgId,
  onClose,
}: {
  tutor: Pick<PersonRow, "id" | "kind" | "first_name" | "last_name">;
  hasAccount: boolean;
  /** The payroll bank is set up. */
  done?: boolean;
  orgId: number;
  onClose: () => void;
}) {
  const { account } = useContext(OrgRolesAndAccountContext);
  const name = personName(tutor) || "The tutor";
  const [link, setLink] = useState<string>();
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (done || !hasAccount) return;
    PaymentsService.generateStripeAccountRemediationLink({ tutorId: tutor.id })
      .then(setLink)
      .catch(() => setFailed(true));
  }, [done, hasAccount, tutor.id]);

  function copy() {
    if (!link) return;
    navigator.clipboard
      .writeText(link)
      .then(() => Notifications.success("Link copied."))
      .catch(() => Notifications.error("Could not copy the link."));
  }

  function send() {
    if (!link) return;
    setSending(true);
    MessagesService.createMessageToRecipients({
      requestBody: {
        m_type: MessageType.DIRECT,
        direction: MessageDirection.OUTBOUND,
        subject: "Set up your bank account for payroll",
        content: `Please add the bank account your pay goes to: ${link}`,
        sender_id: account?.reference_id,
        org_id: orgId,
        email: true,
        sms: true,
        recipient_types: [OrgMessageRecipient.TUTORS],
        tutor_recipients: [tutor.id],
      },
    })
      .then(() => {
        setSent(true);
        Notifications.success(`Sent to ${name}.`);
      })
      .catch(() => Notifications.error("Could not send the link."))
      .finally(() => setSending(false));
  }

  const reason = !hasAccount
    ? `${name} has no Tadpole account yet: the link can be made once they accept their invite.`
    : failed
    ? "The bank setup link could not be made. Try again in a moment."
    : undefined;

  if (done) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="dialog-content max-w-[480px]">
          <h2 className="text-lg font-bold">Payroll</h2>
          <p className="mt-2 text-sm text-gray-700">
            {name}'s payroll bank is set up.
          </p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="dialog-content max-w-[520px]">
        <h2 className="text-lg font-bold">Bank setup link</h2>
        <p className="mt-1 text-sm text-gray-600">
          {name} adds the bank their pay goes to with this link.
        </p>
        {reason ? (
          <p className="mt-4 text-sm text-gray-700" role="note">
            {reason}
          </p>
        ) : (
          <div className="mt-4 flex gap-2">
            <input
              readOnly
              aria-label="Bank setup link"
              className="h-8 min-w-0 flex-1 rounded-md border border-gray-300 bg-gray-50 px-2 text-sm"
              value={link ?? "Making the link…"}
            />
            <Button
              color={ButtonColor.PURPLE}
              fill={ButtonFill.HOLLOW}
              disabled={!link}
              onClick={copy}
            >
              Copy
            </Button>
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-3">
          {!hasAccount && (
            <span className="text-xs text-gray-500">
              Send needs their account.
            </span>
          )}
          <Button
            color={!link || sent ? ButtonColor.GRAY : ButtonColor.GREEN}
            disabled={!link || sending || sent}
            onClick={send}
          >
            {sent ? "Sent" : `Send to ${name}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
